import { Timestamp } from 'firebase/firestore';
import type { VacationRequest, VacationRequestStatus } from '../models/vacation.types';
import type { VacationEvent } from '../models/vacationEvent.types';
import { buildVacationTimeline, type VacationTimelineEvent } from './vacationTimeline';
import {
    buildCorrectionNote,
    buildRecordedEventLabel,
    FRUITION_EVENT_TYPES,
    readRevisionFromId,
    resolveEventTimelineKey,
    resolveEventTone,
} from './vacationEventLabels';

const OPEN_STATUSES: readonly VacationRequestStatus[] = [
    'Draft',
    'PendingManager',
    'PendingClient',
    'PendingDP',
    'PendingCorrection',
];

interface HistoryItem {
    event: VacationTimelineEvent;
    flowKey: string;
}

function toDate(value: unknown): Date | null {
    if (value instanceof Date) return value;
    if (value instanceof Timestamp) return value.toDate();
    if (value && typeof (value as Timestamp).toDate === 'function') return (value as Timestamp).toDate();
    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
}

function timeOf(event: VacationEvent): number {
    return toDate(event.createdAt)?.getTime() ?? 0;
}

function dedupKey(event: VacationEvent): string {
    const isSegmentDecision = /_segment_(approved|rejected)$/.test(event.type);
    return isSegmentDecision ? event.id.replace(/_r\d+(?=_)/, '') : event.id;
}

export function dedupeRecordedEvents(events: VacationEvent[]): VacationEvent[] {
    const earliest = new Map<string, VacationEvent>();
    events.forEach((event) => {
        const key = dedupKey(event);
        const current = earliest.get(key);
        if (!current || timeOf(event) < timeOf(current)) earliest.set(key, event);
    });
    return Array.from(earliest.values());
}

export function inferLegacyCorrections(
    events: VacationEvent[],
    request: VacationRequest
): VacationEvent[] {
    const recordedRevisions = new Set(
        events
            .filter((event) => event.type === 'correction_submitted')
            .map((event) => event.payload?.revision)
    );
    const lowestRevisionByKey = new Map<string, number>();
    events.forEach((event) => {
        const revision = readRevisionFromId(event.id);
        if (revision === null) return;
        const key = dedupKey(event);
        lowestRevisionByKey.set(key, Math.min(revision, lowestRevisionByKey.get(key) ?? revision));
    });

    const markers = new Map<number, { time: number; isCopy: boolean }>();
    events.forEach((event) => {
        const revision = readRevisionFromId(event.id);
        if (!revision || recordedRevisions.has(revision)) return;
        const isCopy = (lowestRevisionByKey.get(dedupKey(event)) ?? revision) < revision;
        const time = isCopy ? timeOf(event) : timeOf(event) - 1;
        const current = markers.get(revision);
        const better =
            !current || (isCopy && !current.isCopy) || (isCopy === current.isCopy && time < current.time);
        if (better) markers.set(revision, { time, isCopy });
    });

    return Array.from(markers.entries()).map(([revision, marker]) => ({
        id: `correction_submitted_r${revision}_inferred`,
        requestId: request.id,
        type: 'correction_submitted',
        actor: { email: request.collaboratorEmail },
        note: null,
        source: 'user',
        payload: { revision },
        createdAt: new Date(marker.time),
    }));
}

function withInferredRevision(event: VacationEvent, corrections: VacationEvent[]): VacationEvent {
    if (typeof event.payload?.revision === 'number') return event;
    const revision = corrections.filter((correction) => timeOf(correction) <= timeOf(event)).length;
    return revision > 0 ? { ...event, payload: { ...event.payload, revision } } : event;
}

function toTimelineEvent(event: VacationEvent, request: VacationRequest): VacationTimelineEvent {
    return {
        key: event.id,
        label: buildRecordedEventLabel(event, request),
        tone: resolveEventTone(event.type),
        occurredAt: toDate(event.createdAt),
        hasTime: true,
        actorEmail: event.actor?.email ?? null,
        note: event.type === 'correction_submitted' ? buildCorrectionNote(event) : event.note ?? null,
    };
}

function dropRedundantDecisions(events: VacationEvent[]): VacationEvent[] {
    const segmentRevisions = new Set(
        events
            .filter((event) => event.type === 'dp_segment_approved' || event.type === 'dp_segment_rejected')
            .map((event) => event.payload?.revision)
    );
    const managerRejectedRevisions = new Set(
        events.filter((event) => event.type === 'manager_rejected').map((event) => event.payload?.revision)
    );

    return events.filter((event) => {
        const isGenericDpDecision = event.type === 'dp_approved' || event.type === 'dp_rejected';
        if (isGenericDpDecision && segmentRevisions.has(event.payload?.revision)) return false;
        if (event.type === 'sell_rejected' && managerRejectedRevisions.has(event.payload?.revision)) return false;
        return true;
    });
}

function compareNewestFirst(flowRank: Map<string, number>) {
    return (a: HistoryItem, b: HistoryItem): number => {
        const timeA = a.event.occurredAt?.getTime() ?? Number.NEGATIVE_INFINITY;
        const timeB = b.event.occurredAt?.getTime() ?? Number.NEGATIVE_INFINITY;
        if (timeA !== timeB) return timeB - timeA;
        return (flowRank.get(b.flowKey) ?? 0) - (flowRank.get(a.flowKey) ?? 0);
    };
}

export function buildVacationHistory(
    request: VacationRequest,
    recorded: VacationEvent[],
    today: Date = new Date()
): VacationTimelineEvent[] {
    const derived = buildVacationTimeline(request, today);
    const flowRank = new Map(derived.map((event, index) => [event.key, index]));
    const isOpen = OPEN_STATUSES.includes(request.status);

    const relevant = recorded.filter((event) => !FRUITION_EVENT_TYPES.includes(event.type));
    const corrections = [
        ...relevant.filter((event) => event.type === 'correction_submitted'),
        ...inferLegacyCorrections(relevant, request),
    ];
    const withCorrections = [
        ...dedupeRecordedEvents(relevant.filter((event) => event.type !== 'correction_submitted')).map(
            (event) => withInferredRevision(event, corrections)
        ),
        ...corrections,
    ];
    const recordedKeys = new Set(withCorrections.map((event) => resolveEventTimelineKey(event.type)));
    const visibleEvents = dropRedundantDecisions(withCorrections);
    const isManagerRejectedSell =
        request.managerApproval.status === 'Rejected' && request.sellRequest.status === 'Rejected';

    const pending = derived.filter((event) => event.tone === 'pending' && isOpen);
    const derivedFacts = derived.filter(
        (event) =>
            event.tone !== 'pending' &&
            !recordedKeys.has(event.key) &&
            !(event.key === 'sell' && isManagerRejectedSell)
    );

    const facts: HistoryItem[] = [
        ...derivedFacts.map((event) => ({ event, flowKey: event.key })),
        ...visibleEvents.map((event) => ({
            event: toTimelineEvent(event, request),
            flowKey: resolveEventTimelineKey(event.type),
        })),
    ].sort(compareNewestFirst(flowRank));

    return [...pending, ...facts.map((item) => item.event)];
}
